import React from 'react'
import { SvgIcon } from 'src/components/icons/svg-icon'
import { CLASS_PREFIX } from 'src/constants'
import { textResources } from 'src/lang/de'
import './project_download_files.scss'

const cls = `${CLASS_PREFIX}project-download-files`

const t = textResources.projects

export type ProjectDownloadFile = {
  id: string;
  altText?: string | null | undefined;
  name?: string | null | undefined;
  source?: string | null | undefined;
  file: {
      __typename?: 'Blob' | undefined;
      url: string;
      previewUrl: string;
      contentType: string;
      byteSize: number;
  };
};

type Props = {
  files: ProjectDownloadFile[];
};

const formatFileSize = (bytes?: number): string | null => {
  if (typeof bytes !== 'number' || Number.isNaN(bytes) || bytes < 0) {
    return null
  }

  if (bytes < 1024) {
    return `${bytes} B`
  }

  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value = value / 1024
    unitIndex += 1
  }

  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`
}

const FILE_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG'
}

const formatFileType = (contentType: string): string => {
  return FILE_TYPE_LABELS[contentType] ?? contentType
}

export const ProjectDownloadFiles: React.FC<Props> = ({ files }) => {
  if (files.length === 0) {
    return <p className={`${cls}__empty`}>{t.featureTabs.noDownloadsAvailable}</p>
  }

  return (
    <div className={cls}>
      {files.map((file) => {
        const formattedFileSize = formatFileSize(file.file.byteSize)
        const formattedFileType = formatFileType(file.file.contentType)

        return (
          <article className={`${cls}__card`} key={file.id}>
            <div className={`${cls}__preview`}>
              <img src={file.file.previewUrl} alt={file.altText ?? ''} loading="lazy" />
            </div>

            <div className={`${cls}__content`}>
              <h3 className={`${cls}__title`}>{file.name}</h3>

              <div className={`${cls}__bottom`}>
                <div className={`${cls}__meta`}>
                  <span>{'(' + formattedFileType + ' / ' + formattedFileSize + ')'}</span>
                </div>

                <a
                  className={`${cls}__download-btn`}
                  href={file.file.url ?? ''}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Download ${file.name}`}
                >
                  <SvgIcon name="General/download-03" />
                  <span className={`${cls}__download-btn-text`}>{t.featureTabs.downloadButtonLabel}</span>
                </a>
              </div>
            </div>
          </article>
        )
      })}
    </div>
  )
}
